import { memo, useCallback, useMemo } from 'react';
import { SingularStationArea, StationSpec } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { SelectInput } from '@/modules/common/components/inputs';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumUtils } from '@/modules/common/utils';

const LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS: {
  label: string;
  value: StationSpec['loadHandlingDurationStrategyInSimulation'];
}[] = [
  { label: 'By height', value: 'BY_HEIGHT' },
  { label: 'By override', value: 'BY_OVERRIDE' },
];

type Props = {
  selectedSingularStationAreas: readonly SingularStationArea[];
};

const canEditLoadDuration = (loadHandlingType: SingularStationArea['loadHandlingType']) =>
  loadHandlingType === 'INTAKE' ||
  loadHandlingType === 'STORAGE' ||
  loadHandlingType === 'OFF_VEHICLE_PROCESSING';

const canEditUnloadDuration = (loadHandlingType: SingularStationArea['loadHandlingType']) =>
  loadHandlingType === 'DELIVERY' ||
  loadHandlingType === 'STORAGE' ||
  loadHandlingType === 'OFF_VEHICLE_PROCESSING';

const canEditProcessingDuration = (loadHandlingType: SingularStationArea['loadHandlingType']) =>
  loadHandlingType === 'OFF_VEHICLE_PROCESSING' || loadHandlingType === 'ON_VEHICLE_PROCESSING';

function SingularStationSpecPropertiesComponent({ selectedSingularStationAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const area =
    selectedSingularStationAreas.length === 1 ? selectedSingularStationAreas[0] : undefined;

  const onZChange = useCallback(
    (newValueMm: number) => {
      if (!area || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('singularStationArea.stationSpec.z', newValue);

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          z: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  const onStrategyChange = useCallback(
    (newValue: StationSpec['loadHandlingDurationStrategyInSimulation']) => {
      if (!area) return;

      fddmSvc.setPreference(
        'singularStationArea.stationSpec.loadHandlingDurationStrategyInSimulation',
        newValue,
      );

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          loadHandlingDurationStrategyInSimulation: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  const onLoadDurationChange = useCallback(
    (newValue: number) => {
      if (!area || Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.stationSpec.loadDurationOverrideInSimulation',
        newValue,
      );

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          loadDurationOverrideInSimulation: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  const onUnloadDurationChange = useCallback(
    (newValue: number) => {
      if (!area || Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.stationSpec.unloadDurationOverrideInSimulation',
        newValue,
      );

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          unloadDurationOverrideInSimulation: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  const onProcessingDurationChange = useCallback(
    (newValue: number) => {
      if (!area || Number.isNaN(newValue)) return;

      fddmSvc.setPreference(
        'singularStationArea.stationSpec.processingExtension.processingDurationInSimulation',
        newValue,
      );

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          processingExtension: {
            ...area.stationSpec.processingExtension,
            processingDurationInSimulation: newValue,
          },
        },
      });
    },
    [area, fddmSvc],
  );

  if (!area) return null;

  const { stationSpec, loadHandlingType } = area;
  const showOverrideFields = stationSpec.loadHandlingDurationStrategyInSimulation === 'BY_OVERRIDE';

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.load_elevation'>
        <NumberInput
          value={NumUtils.metersToMmRounded(stationSpec.z)}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onZChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.load_duration_strategy'>
        <SelectInput
          options={LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS}
          onChange={onStrategyChange}
          value={stationSpec.loadHandlingDurationStrategyInSimulation}
        />
      </PropertiesLabel>

      {showOverrideFields && canEditLoadDuration(loadHandlingType) && (
        <PropertiesLabel i18nkey='interface:properties.area.load_duration'>
          <NumberInput
            value={stationSpec.loadDurationOverrideInSimulation ?? 0}
            unit='sec'
            prefix={<GapHorizontalIcon />}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onLoadDurationChange}
          />
        </PropertiesLabel>
      )}

      {showOverrideFields && canEditUnloadDuration(loadHandlingType) && (
        <PropertiesLabel i18nkey='interface:properties.area.unload_duration'>
          <NumberInput
            value={stationSpec.unloadDurationOverrideInSimulation ?? 0}
            unit='sec'
            prefix={<GapHorizontalIcon />}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onUnloadDurationChange}
          />
        </PropertiesLabel>
      )}

      {canEditProcessingDuration(loadHandlingType) && (
        <PropertiesLabel i18nkey='interface:properties.area.operation_time'>
          <NumberInput
            value={stationSpec.processingExtension?.processingDurationInSimulation ?? 0}
            unit='sec'
            prefix={<GapHorizontalIcon />}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onProcessingDurationChange}
          />
        </PropertiesLabel>
      )}
    </>
  );
}

export const StationSpecProperties = memo(SingularStationSpecPropertiesComponent);
